import type { TokenProvider } from '../../auth/tokenProvider';
import { ApiError } from './apiError';

/**
 * The ONE and ONLY module in the codebase that performs network I/O (fetch).
 *
 * Every store / inference / knowledge client calls {@link ApiClient.post}; they
 * never touch fetch directly. This is the strict layering boundary.
 *
 * Pure TS — no React.
 */

export interface ApiClientConfig {
  /** Absolute base URL (no trailing slash), from getApiConfig(stage). */
  baseUrl: string;
  /** Supplies the Cognito bearer token attached to every request. */
  tokenProvider: TokenProvider;
}

/** Shape of the modeled EngineeringVectorServiceException error body. */
interface ServiceExceptionBody {
  message?: string;
  exceptionType?: string;
  resourceType?: string;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokenProvider: TokenProvider;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.tokenProvider = config.tokenProvider;
  }

  /**
   * POSTs a JSON body to `path` and returns the parsed JSON response.
   *
   * On a non-2xx response, parses the modeled EngineeringVectorServiceException
   * and throws a typed {@link ApiError}.
   */
  async post<TReq, TRes>(path: string, body: TReq): Promise<TRes> {
    const token = await this.tokenProvider.getToken();
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      // The deployed API Gateway Cognito authorizer's identity source is
      // `method.request.header.auth`, so the token MUST travel in the `auth`
      // header as a raw JWT (not `Authorization: Bearer`). CORS on the API
      // explicitly allows the `auth` header.
      headers.auth = token;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const text = await response.text();

    if (!response.ok) {
      let parsed: ServiceExceptionBody = {};
      try {
        parsed = text ? (JSON.parse(text) as ServiceExceptionBody) : {};
      } catch {
        parsed = { message: text || response.statusText };
      }
      throw new ApiError(
        response.status,
        parsed.message ?? response.statusText,
        parsed.exceptionType,
        parsed.resourceType,
      );
    }

    return (text ? JSON.parse(text) : {}) as TRes;
  }
}
